import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { ApiResponse } from '../../models/api-response.model';
import { environment } from '../../../../environments/environment';
import { UpdateEmployerTypeRequest } from '../../models/update-employer-type-request';
import { PaginatedResponse } from '../../models/paginated-response';

export interface ReviewRequest {
  Id: number;
  IsApproved: boolean;
  ReviewerRemark?: string | null;
  ReviewedBy: number;
}

@Injectable({
  providedIn: 'root'
})
export class CompanyService {
  private readonly apiUrl = `${environment.apiGatewayBaseUrl}/Companies`;

  constructor(private http: HttpClient) { }

  getExistingCompanies(): Observable<any> {
    return this.http.get<any>(`${this.apiUrl}/profiles`);
  }

  getAllCompanyOnboardingRequests(): Observable<any> {
    return this.http.get<any>(`${this.apiUrl}/onboarding-requests`);
  }

  getExistingCompaniesPaginated(
    pageNumber?: number,
    pageSize?: number,
    employerTypeId?: number | string | null,
    companyId?: string,
    directorNic?: string
  ): Observable<PaginatedResponse<any>> {
    let params = new HttpParams();
    if (pageNumber != null) params = params.set('pageNumber', pageNumber.toString());
    if (pageSize != null) params = params.set('pageSize', pageSize.toString());
    // Fixed filters per requirement
    params = params.set('isActive', 'true');
    params = params.set('isApproved', 'true');
    params = params.set('isAuthorized', 'true');
    if (employerTypeId !== '' && employerTypeId != null)
      params = params.set('employerTypeId', employerTypeId.toString());
    if (companyId !== '' && companyId != null)
      params = params.set('companyId', companyId);
    if (directorNic !== '' && directorNic != null)
      params = params.set('directorNic', directorNic);

    return this.http.get<PaginatedResponse<any>>(`${this.apiUrl}/profiles/paginated`, { params });
  }

  getAllCompanyOnboardingRequestsPaginated(
    pageNumber?: number,
    pageSize?: number
  ): Observable<PaginatedResponse<any>> {
    let params = new HttpParams();
    if (pageNumber != null) params = params.set('pageNumber', pageNumber.toString());
    if (pageSize != null) params = params.set('pageSize', pageSize.toString());

    return this.http.get<PaginatedResponse<any>>(`${this.apiUrl}/onboarding-requests/paginated`, { params });
  }

  authorizeCompanyOnboardingRequestAsync(companyId: number, request: any): Observable<ApiResponse<boolean>> {
    return this.http.post<ApiResponse<boolean>>(`${this.apiUrl}/onboarding-requests/authorize/${companyId}`, request);
  }

  /** GET /Companies/{companyId}/profile */
  getCompanyProfile(companyId: number): Observable<ApiResponse<any>> {
    return this.http.get<ApiResponse<any>>(`${this.apiUrl}/${companyId}/profile`);
  }

  /** GET /Companies/{companyId}/profile-documents/view?fileName=...  -> returns the PDF/file as a Blob */
  viewProfileDocument(companyId: number, fileName: string) {
    return this.http.get(`${this.apiUrl}/${companyId}/profile-documents/view`, {
      params: { fileName },
      responseType: 'blob'
    });
  }

  /** Convenience: build a direct URL you can use in <a [href]> or window.open(...) */
  getProfileDocumentUrl(companyId: number, fileName: string): string {
    return `${this.apiUrl}/${companyId}/profile-documents/view?fileName=${encodeURIComponent(fileName)}`;
  }


  // ───────────────────────────────────────────────────────────────
  // ⬛ Review endpoints (all POST with the same body)
  // ───────────────────────────────────────────────────────────────

  /** POST /Companies/{companyId}/contact-details/review */
  reviewContactDetails(companyId: number, body: ReviewRequest): Observable<ApiResponse<boolean>> {
    return this.http.post<ApiResponse<boolean>>(`${this.apiUrl}/${companyId}/contact-details/review`, body);
  }

  /** POST /Companies/{companyId}/financial-snapshot/review */
  reviewFinancialSnapshot(companyId: number, body: ReviewRequest): Observable<ApiResponse<boolean>> {
    return this.http.post<ApiResponse<boolean>>(`${this.apiUrl}/${companyId}/financial-snapshot/review`, body);
  }

  /** POST /Companies/{companyId}/authorized-contact/review */
  reviewAuthorizedContact(companyId: number, body: ReviewRequest): Observable<ApiResponse<boolean>> {
    return this.http.post<ApiResponse<boolean>>(`${this.apiUrl}/${companyId}/authorized-contact/review`, body);
  }

  /** POST /Companies/{companyId}/secretary/review */
  reviewSecretary(companyId: number, body: ReviewRequest): Observable<ApiResponse<boolean>> {
    return this.http.post<ApiResponse<boolean>>(`${this.apiUrl}/${companyId}/secretary/review`, body);
  }

  /** POST /Companies/{companyId}/auditor/review */
  reviewAuditor(companyId: number, body: ReviewRequest): Observable<ApiResponse<boolean>> {
    return this.http.post<ApiResponse<boolean>>(`${this.apiUrl}/${companyId}/auditor/review`, body);
  }

  /** POST /Companies/{companyId}/documents/review — pass the single document Id in body.Id */
  reviewDocuments(companyId: number, body: ReviewRequest): Observable<ApiResponse<boolean>> {
    return this.http.post<ApiResponse<boolean>>(`${this.apiUrl}/${companyId}/documents/review`, body);
  }

  /** POST /Companies/{companyId}/bank-accounts/review — pass the single account Id in body.Id */
  reviewBankAccounts(companyId: number, body: ReviewRequest): Observable<ApiResponse<boolean>> {
    return this.http.post<ApiResponse<boolean>>(`${this.apiUrl}/${companyId}/bank-accounts/review`, body);
  }

  /** POST /Companies/{companyId}/directors/review — pass the single director Id in body.Id */
  reviewDirectors(companyId: number, body: ReviewRequest): Observable<ApiResponse<boolean>> {
    return this.http.post<ApiResponse<boolean>>(`${this.apiUrl}/${companyId}/directors/review`, body);
  }

  /** POST /Companies/{companyId}/shareholders/review — pass the single shareholder Id in body.Id */
  reviewShareholders(companyId: number, body: ReviewRequest): Observable<ApiResponse<boolean>> {
    return this.http.post<ApiResponse<boolean>>(`${this.apiUrl}/${companyId}/shareholders/review`, body);
  }

  /** PUT /Companies/update-employer-type */
  updateEmployerType(request: UpdateEmployerTypeRequest): Observable<ApiResponse<boolean>> {
    return this.http.put<ApiResponse<boolean>>(
      `${this.apiUrl}/update-employer-type`,
      {
        companyId: request.companyId,
        employerTypeId: request.employerTypeId,
        isEligibleForPayRoll: request.isEligibleForPayRoll,
        isPayRollGrantedByAdmin: request.isPayRollGrantedByAdmin
      }
    );
  }

  /** POST /Company/SaveCompanyEWWBlock */
  saveCompanyEWWBlock(payload: { companyId: number; isBlocked: boolean; blockReason: string | null }): Observable<ApiResponse<string>> {
    return this.http.post<ApiResponse<string>>(
      `${environment.apiGatewayBaseUrl}/Company/SaveCompanyEWWBlock`,
      payload
    );
  }
}
